import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { AppService } from '../../../../../shared/services';
import { AppHttpService } from '../../../../../shared/services/app-http.service';
import { FetchWarrantyClaimDetailRequest } from '../../../core/projects.actions';
import { warrantyClaimDetail, warrantiesInfo } from '../../../core/projects.selectors';
import { ProjectsService } from '../../../core/projects.service';
import { WarrantyClaimDetailComponent } from '../../../pages/projects-details-page/components/tab-screens/warranty/warranty-claim-detail.component';
import { WarrantyClaimFormComponent } from '../../../pages/projects-details-page/components/tab-screens/warranty/warranty-claim-form.component';
import { issueStatusText } from '../../../../../shared/helpers/issue-workflow';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'svg'];
const VIDEO_EXTS = ['mp4', 'mov', 'm4v', 'webm', 'avi', 'mkv'];
const DOCUMENT_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'];

function deriveFileType(item: any): 'picture' | 'video' | 'document' {
  if (item?.fileType) return item.fileType;
  const mime = (item?.mime || item?.mime_type || item?.content_type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'picture';
  if (mime.startsWith('video/')) return 'video';
  if (mime) return 'document';
  const name = (item?.original_name || item?.name || item?.filename || item?.original_url || '').toLowerCase();
  const ext = name.split('?')[0].split('.').pop() || '';
  if (IMAGE_EXTS.includes(ext)) return 'picture';
  if (VIDEO_EXTS.includes(ext)) return 'video';
  if (DOCUMENT_EXTS.includes(ext)) return 'document';
  return 'picture';
}

function normalizeMedia(item: any): any {
  const fileType = deriveFileType(item);
  return {
    ...item,
    fileType,
    preview_url: item.preview_url || item.preview || item.original_url || item.thumb_url || '',
  };
}

@Component({
  selector: 'app-claim-detail-sidenav',
  templateUrl: './claim-detail-sidenav.component.html',
  styleUrls: ['./claim-detail-sidenav.component.scss'],
  standalone: false,
})
export class ClaimDetailSidenavComponent implements OnInit, OnChanges, OnDestroy {
  /** A linked issue's status as its badge prints it (a contractor-workflow stage in words). */
  readonly issueStatusText = issueStatusText;

  @Input() projectId: string;
  @Input() claim: any;
  @Output() closed = new EventEmitter<void>();

  loadedClaim: any = null;
  linkedIssues: any[] = [];
  linkedIssuesLoading = false;
  private warranties: any[] = [];

  get mediaItems(): any[] {
    const all = [
      ...((this.loadedClaim?.pictures as any[]) || []),
      ...((this.loadedClaim?.attachments as any[]) || []),
    ];
    return all.map(normalizeMedia);
  }

  get pictureItems(): any[] {
    return this.mediaItems.filter(m => m.fileType === 'picture');
  }

  downloadAllPictures(): void {
    const pics = this.pictureItems;
    if (!pics.length) return;
    pics.forEach((pic, idx) => {
      const url = pic.original_url || pic.thumb_url;
      if (!url) return;
      const name = pic.original_name || `${this.loadedClaim?.claim_number || 'claim'}-${idx + 1}.jpg`;
      saveAs(url, name);
    });
  }

  get subHeaderChips(): string[] {
    const chips: string[] = [];
    const loc = this.loadedClaim?.site?.name
      || this.loadedClaim?.location
      || this.loadedClaim?.station_range
      || this.loadedClaim?.defect_type
      || this.loadedClaim?.claim_type;
    if (loc) chips.push(loc);
    const date = this.loadedClaim?.created_at || this.loadedClaim?.report_date;
    if (date) {
      try {
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
          chips.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
        }
      } catch (e) { /* ignore */ }
    }
    return chips;
  }

  severityMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    low: { label: 'Low', bgColor: 'bg-info', textColor: 'txt-info' },
    medium: { label: 'Medium', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    high: { label: 'High', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    critical: { label: 'Critical', bgColor: 'bg-critical', textColor: 'txt-critical' },
  };

  statusMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    draft: { label: 'Draft', bgColor: 'bg-draft', textColor: 'txt-draft' },
    sent_back: { label: 'Sent back to inspector', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    submitted: { label: 'Submitted', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    pm_review: { label: 'PM Review', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    approved: { label: 'Approved', bgColor: 'bg-approved', textColor: 'txt-approved' },
    rejected: { label: 'Rejected', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    sent_to_contractor: { label: 'Sent to contractor', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    contractor_notified: { label: 'Sent to contractor', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    in_progress: { label: 'In Progress', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    responded: { label: 'Contractor responded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    verified: { label: 'Verified', bgColor: 'bg-approved', textColor: 'txt-approved' },
    disputed: { label: 'Disputed', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    remediation_complete: { label: 'Remediation Complete', bgColor: 'bg-approved', textColor: 'txt-approved' },
    closed: { label: 'Closed', bgColor: 'bg-approved', textColor: 'txt-approved' },
  };

  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    public appService: AppService,
    private appHttpService: AppHttpService,
    private projectsService: ProjectsService,
  ) {}

  ngOnInit() {
    this.loadedClaim = this.claim;
    this.fetchClaimAndIssues();

    this.store.select(warrantyClaimDetail)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(detail => {
        if (detail && detail.id === this.loadedClaim?.id) {
          this.loadedClaim = { ...detail, ...(detail.attributes || {}) };
        }
      });

    this.store.select(warrantiesInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        this.warranties = info?.list || [];
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.claim && !changes.claim.firstChange) {
      this.loadedClaim = this.claim;
      this.linkedIssues = [];
      this.fetchClaimAndIssues();
    }
  }

  private fetchClaimAndIssues(): void {
    if (!this.loadedClaim?.id) return;
    this.store.dispatch(FetchWarrantyClaimDetailRequest({ payload: { claimId: this.loadedClaim.id } }));
    this.fetchLinkedIssues(this.loadedClaim.id);
  }

  fetchLinkedIssues(claimId: number | string): void {
    this.linkedIssuesLoading = true;
    this.appHttpService.getService(`/v1/warranty/claims/${claimId}/issues`).subscribe({
      next: (resp: any) => {
        const issues = resp?.data || resp || [];
        this.linkedIssues = issues.map(i => ({
          ...i,
          _expanded: true,
          pictures: (i.pictures || []).map(normalizeMedia),
        }));
        this.linkedIssuesLoading = false;
      },
      error: () => {
        this.linkedIssues = [];
        this.linkedIssuesLoading = false;
      },
    });
  }

  openFullClaim(): void {
    if (!this.loadedClaim?.id) return;
    const status = this.loadedClaim.status;
    const isEditable = status === 'draft' || status === 'sent_back';
    this.router.navigate([`/projects/${this.projectId}`], {
      queryParams: { tab: 'warranty', subTab: 'claims', claimId: this.loadedClaim.id },
    }).then(() => {
      this.projectsService.openRightPanel.next(
        isEditable
          ? {
              component: WarrantyClaimFormComponent,
              data: {
                warranties: this.warranties,
                projectId: this.projectId,
                editClaim: { ...this.loadedClaim },
              },
            }
          : {
              component: WarrantyClaimDetailComponent,
              data: { ...this.loadedClaim },
            },
      );
    });
    this.closed.emit();
  }

  onClose(): void {
    this.closed.emit();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
